import React from 'react';
import { Accordion, Box, Divider, TextLink, Typography } from '@drone-js/core';
import MainCategoryFilterItem from 'component/page/search/SortAndFilter/CategoryWithSubcategoryFilter/MainCategoryFilterItem';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesCategoriesAccordionClickEventActivitiesFieldsV2,
    ActivitiesCategoriesAccordionSeenEventActivitiesFieldsV2,
    ActivitiesCategoriesFilterClickEventActivitiesFieldsV6,
    ActivitiesClearAllCategoriesButtonClickEventActivitiesFieldsV2,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesCategoriesAccordionClickEventActivitiesManifestV2,
    ActivitiesCategoriesAccordionSeenEventActivitiesManifestV2,
    ActivitiesCategoriesFilterClickEventActivitiesManifestV6,
    ActivitiesClearAllCategoriesButtonClickEventActivitiesManifestV2,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { CategoryWithSubcategoryFilterProps } from 'component/page/search/SortAndFilter/CategoryWithSubcategoryFilter/CategoryWithSubcategoryFilter.types';
import SubcategoryFilterItems from 'component/page/search/SortAndFilter/CategoryWithSubcategoryFilter/SubcategoryFilterItems';
import { SubcategoryId } from 'core/type/Subcategory.types';
import { CategoryId } from 'core/type/Category.types';
import { InView } from 'react-intersection-observer';
import { SortAndFilterContentVariant } from 'component/page/search/SortAndFilter/SortAndFilterContent.selectors';

const CategoryWithSubcategoryFilter: React.FC<CategoryWithSubcategoryFilterProps> = React.memo((props) => {
    const [expandedCategories, setExpandedCategories] = React.useState<CategoryId[]>([]);
    const isSidebar = props.variant === SortAndFilterContentVariant.Sidebar;
    const isFilterBottomSheet = props.variant === SortAndFilterContentVariant.CategoryBottomSheet;

    const onSeenAccordion = (inView: boolean, categoryId: CategoryId, index: number) => {
        if (inView) {
            trackElementEvent<ActivitiesCategoriesAccordionSeenEventActivitiesFieldsV2>(ActivitiesCategoriesAccordionSeenEventActivitiesManifestV2.ids, {
                activities_categories: categoryId.toString(),
                action_element_index: index + 1,
                action_element_value: 'collapsed',
                is_sidebar: isSidebar,
                is_filter_bottom_sheet: isFilterBottomSheet,
            });
        }
    };

    const onChange = (value: CategoryId, index: number, count: number, label: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        trackElementEvent<ActivitiesCategoriesFilterClickEventActivitiesFieldsV6>(
            ActivitiesCategoriesFilterClickEventActivitiesManifestV6.ids,
            {
                is_categories_tab: false,
                order_index: index,
                activities_count: count,
                is_sidebar: isSidebar,
                is_filter_bottom_sheet: isFilterBottomSheet,
            },
            { action_element_value: label, action_element_index: index + 1 },
        );
        props.onCategoriesChange(value, event.target.checked);
    };

    const onSubcategoryClick = (event: React.ChangeEvent<HTMLInputElement>, value: SubcategoryId) => {
        props.onSubcategoriesChange(value, event.target.checked);
    };

    const onToggleAccordion = (isExpanded: boolean, index: number, categoryId: CategoryId) => {
        trackElementEvent<ActivitiesCategoriesAccordionClickEventActivitiesFieldsV2>(ActivitiesCategoriesAccordionClickEventActivitiesManifestV2.ids, {
            activities_categories: categoryId.toString(),
            action_element_index: index + 1,
            action_element_value: isExpanded ? 'expanded' : 'collapsed',
            is_sidebar: isSidebar,
            is_filter_bottom_sheet: isFilterBottomSheet,
        });
        if (isExpanded) {
            setExpandedCategories((prev) => [...prev, categoryId]);
        } else {
            setExpandedCategories((prev) => prev.filter((item) => item !== categoryId));
        }
    };

    const onClickClearCategoryFilter = () => {
        trackElementEvent<ActivitiesClearAllCategoriesButtonClickEventActivitiesFieldsV2>(ActivitiesClearAllCategoriesButtonClickEventActivitiesManifestV2.ids, {
            is_sidebar: isSidebar,
            is_filter_bottom_sheet: isFilterBottomSheet,
        });
        props.onClearCategoryFilter();
    };

    const categoryFilterWithoutAll = props.items.filter((item) => item.value.length > 0);
    const isCategoryExpanded = (categoryId: CategoryId) => expandedCategories.includes(categoryId);

    return (
        <>
            <Box px="16">
                <Typography data-testid="activities-categories-filter-heading" variant="heading-default" role="heading" aria-level={2} pb="8">
                    {props.cms[252193]}
                </Typography>
                <Box py="16">
                    <TextLink
                        data-element-name="activities-clear-all-categories-button"
                        data-testid="activities-clear-all-categories-button"
                        hierarchy="primary"
                        label={props.cms[345125]}
                        trailingIcon="fill.arrow.loop"
                        onClick={onClickClearCategoryFilter}
                        textVariant="body-s-strong"
                    />
                </Box>
            </Box>
            {categoryFilterWithoutAll.map((category, key) => (
                <div key={key}>
                    <Box px="16">
                        <Divider />
                    </Box>
                    {category.subcategories.length > 0 ? (
                        <InView onChange={(inView) => onSeenAccordion(inView, category.value, key)} triggerOnce={true} threshold={1}>
                            <Accordion
                                key={key}
                                data-element-name="activities-categories-accordion"
                                data-testid="activities-categories-accordion"
                                actionType="chevron-icon"
                                contentSummary={
                                    <MainCategoryFilterItem item={category} onCategoriesChange={onChange(category.value, key, category.count, category.label)} index={key} />
                                }
                                level={4}
                                view={isCategoryExpanded(category.value) ? 'expanded' : 'collapsed'}
                                onToggle={(isExpanded) => onToggleAccordion(isExpanded, key, category.value)}
                            >
                                <SubcategoryFilterItems
                                    items={category.subcategories}
                                    onSubcategoriesChange={onSubcategoryClick}
                                    cms={props.cms}
                                    variant={props.variant}
                                    index={key}
                                    categoryId={category.value}
                                />
                            </Accordion>
                        </InView>
                    ) : (
                        <Box py="16" ps="16" pe="48">
                            <MainCategoryFilterItem item={category} onCategoriesChange={onChange(category.value, key, category.count, category.label)} index={key} />
                        </Box>
                    )}
                </div>
            ))}
        </>
    );
});

CategoryWithSubcategoryFilter.displayName = 'CategoryWithSubcategoryFilter';

export default CategoryWithSubcategoryFilter;
